/* eslint-disable camelcase */
import axios from 'axios';
import http from 'http';
import https from 'https';
import queryString from 'qs';
import {
    facets as facetsUtils,
    quickFilters as quickFiltersUtils,
    getFiltersConfig,
} from 'utils';
import { categorySlugifier } from './categorySlugifier';
import tracer from './tracer';

export const MATCHING_FILTERS = {
    brand: 'brand_name',
    seller: 'seller_name',
    price: 'price',
};

const getConditionsFromRawQuickFilters = rawQuickFilters =>
    rawQuickFilters &&
    rawQuickFilters.conditionGroups &&
    rawQuickFilters.conditionGroups[0] &&
    rawQuickFilters.conditionGroups[0].conditions;

export const EXPERIENCES = {
    PAINT: 'paint',
    B2B: 'B2B',
    GUIDED_NAVIGATION: 'guided_navigation',
    MEASUREMENT_PRICE: 'measurement_price',
};

const instance = axios.create({
    httpsAgent: new https.Agent({
        rejectUnauthorized: false,
        keepAlive: true,
    }),

    httpAgent: new http.Agent({
        keepAlive: true,
    }),
});

const MSF_PRODUCT_LISTING_CATEGORY_ENDPOINT = 'api/v1/category';

export const setCertificates = certificates => {
    const { httpsAgent } = instance.defaults;
    httpsAgent.ca = certificates;
};

export const getSeoBlocks = data =>
    (data?.category && {
        title: data.category.title,
        description: data.category.description,
        top_description: data.category.top_description,
    }) ||
    {};

export const getExperiences = data =>
    (data &&
        data.category &&
        Array.isArray(data.category.experiences) &&
        data.category.experiences) ||
    [];

const mapCategories = (categories = []) =>
    categories.map(({ picture_url, name, ...sibling }) => ({
        ...sibling,
        label: name,
        picture_path: picture_url,
    }));

export const getChildCategories = data =>
    (data &&
        data.category &&
        data.category.children &&
        mapCategories(data.category.children)) ||
    [];

export const getSiblingCategories = data =>
    (data &&
        data.category &&
        data.category.siblings &&
        mapCategories(data.category.siblings)) ||
    [];

export const getFilters = (data, isB2B) => {
    /**
     * We need to strip out filters that are not used, but still send by the msf
     */
    const filtersBlockList = ['category_id'];
    const sanitizedFilters =
        data && data.filters
            ? data.filters.filter(({ id }) => !filtersBlockList.includes(id))
            : [];

    const mandatoryFiltersIds = Object.values(getFiltersConfig({ isB2B })).map(
        ({ id }) => id,
    );

    return (
        sanitizedFilters.reduce(
            (filters, { labelKey, label_key, id, ...filterValues }) =>
                mandatoryFiltersIds.includes(id)
                    ? filters
                    : [
                          ...filters,
                          {
                              ...filterValues,
                              // should fix in this ticket (snake_case version should be kept): https://manomano.atlassian.net/browse/SEARCHMM-3337
                              label_key: labelKey || label_key,
                              id: isNaN(id) ? id : parseInt(id, 10),
                          },
                      ],
            [],
        ) || []
    );
};

export const getPromotedFilters = data =>
    (
        (data &&
            data.guided_navigation &&
            data.guided_navigation.promoted_filters) ||
        []
    ).map(
        ({
            picture_url,
            caption_title,
            is_b2b,
            is_b2c,
            condition_groups = [],
            ...promoterFilterValues
        }) => ({
            ...promoterFilterValues,
            pictureUrl: picture_url,
            captionTitle: caption_title,
            isB2b: is_b2b,
            isB2c: is_b2c,
            conditionGroups: condition_groups.map(
                ({
                    picture_url: pictureUrl,
                    conditions = [],
                    ...conditionGroupValues
                }) => ({
                    ...conditionGroupValues,
                    pictureUrl,
                    conditions: conditions.map(
                        ({ attribute_id, ...conditionValues }) => ({
                            ...conditionValues,
                            attributeId: attribute_id,
                        }),
                    ),
                }),
            ),
        }),
    );

export const getQuickFilters = data =>
    (
        (data &&
            data.guided_navigation &&
            data.guided_navigation.quick_links) ||
        []
    ).map(
        ({
            picture_url,
            caption_title,
            is_b2b,
            is_b2c,
            quick_link,
            ...quickLinkValues
        }) => ({
            ...quickLinkValues,
            pictureUrl: picture_url,
            captionTitle: caption_title,
            isB2b: is_b2b,
            isB2c: is_b2c,
            quickLink: quick_link,
        }),
    );

export const getTipsheet = data =>
    data &&
    data.tipsheet && {
        title: data.tipsheet.title,
        // headline_title: '',
        link: data.tipsheet.url,
        author: data.tipsheet.author && {
            name: data.tipsheet.author.name,
            description: data.tipsheet.author.description,
            picture: data.tipsheet.author.picture,
        },
        background_picture: data.tipsheet.background_picture_url,
        category_id: data.tipsheet.category_id,
    };

export const getBreadcrumb = data =>
    (data &&
        data.breadcrumbs &&
        data.breadcrumbs.map(({ id, url, name }) => ({
            id,
            url,
            label: name,
        }))) ||
    [];

export const getAncestors = data =>
    (data &&
        data.category &&
        data.category.ancestors &&
        data.category.ancestors.map(({ id, name }) => ({
            id,
            name,
        }))) ||
    [];

export const getIsomorphicCategory = (data = {}, isB2B) => {
    if (data.content) {
        const transformed = {
            seo_blocks: getSeoBlocks(data.content),
            sibling_categories: getSiblingCategories(data.content),
            child_categories: getChildCategories(data.content),
            experiences: getExperiences(data.content),
            filters: getFilters(data.content, isB2B),
            quick_filters: getQuickFilters(data.content),
            promoted_filters: getPromotedFilters(data.content),
            tipsheet: getTipsheet(data.content),
            breadcrumb: getBreadcrumb(data.content),
            ancestors: getAncestors(data.content),
        };

        return transformed;
    }
    return data;
};

export const fetchCategory = (categoryId, options) => {
    const {
        baseUrl = '',
        certificates,
        userAgent,
        timeout,
        platform = 'fr',
        msfProductListingHostname,
        isB2B,
    } = options;

    const headers = {
        'Content-Type': 'application/json',
    };

    if (userAgent) {
        headers['User-Agent'] = userAgent;
    }

    const pathname = `/${MSF_PRODUCT_LISTING_CATEGORY_ENDPOINT}/${categoryId}?platform=${platform.toLowerCase()}`;
    const url = `${baseUrl}${pathname}`;
    headers.Host = msfProductListingHostname;

    return tracer.wrap(
        'fragment.listing.fetchCategory',
        {
            tags: {
                ...options,
                pathname,
                url,
            },
        },
        () => {
            return instance
                .get(url, {
                    headers,
                    timeout,
                    httpsAgent: new https.Agent({
                        rejectUnauthorized: false,
                        ca: certificates,
                    }),
                })
                .then(({ data }) => getIsomorphicCategory(data, isB2B))
                .catch(err => {
                    console.error(
                        `Fetch for category '${categoryId}' failed: "${err.message}"`,
                    );
                    /**
                     * Those data are the bare skeleton returned by the category API
                     * Returning default values will allow us to skip tests on their existence
                     */
                    return {
                        error: err,
                        sibling_categories: [],
                        child_categories: [],
                        recommended_categories: [],
                        tipsheet: null,
                        filters: [],
                        quick_filters: [],
                        promoted_filters: [],
                        top_sale_article_id: null,
                        experiences: [],
                        ancestors: [],
                        breadcrumb: [],
                        listing_offset: 0,
                        seo_blocks: {
                            title: null,
                            description: null,
                            top_description: null,
                        },
                    };
                });
        },
    );
};

export const fetchInCategorySearch = (categoryIds, options) => {
    const { baseUrl = '', certificates, Host, userAgent, timeout } = options;

    const headers = {
        'Content-Type': 'application/json',
        'X-Forwarded-Port': 443,
        'X-Forwarded-Proto': 'https',
    };
    if (categoryIds.length === 0) return Promise.resolve([]);

    const qs = categoryIds.join(',');

    return tracer.wrap(
        'fragment.listing.fetchCategoryPreviews',
        {
            tags: {
                categoryIds,
                ...options,
            },
        },
        () => {
            if (Host) {
                headers.Host = Host;
            }

            if (userAgent) {
                headers['User-Agent'] = userAgent;
            }

            return instance
                .get(`${baseUrl}/ajax/category-previews/${qs}`, {
                    headers,
                    timeout,
                    httpsAgent: new https.Agent({
                        rejectUnauthorized: false,
                        ca: certificates,
                    }),
                })
                .then(({ data }) => data?.content || [])
                .catch(err => {
                    console.error(
                        `Fetch for category previews '${qs}' failed: "${err.message}"`,
                    );
                });
        },
    );
};

const getFiltersFromCategoryContext = filters =>
    filters.reduce((resultFilters, filterItem) => {
        const filter = { ...filterItem };

        // @TODO temporary hack until we have the proper matching for the color facet in SEARCHMM-1437
        if (filter.label === 'Teinte') {
            filter.label = 'Couleur';
        }

        return [...resultFilters, filter];
    }, []);

const checkAllConditionsExist = ({
    conditions,
    filters,
    filtersConfig,
    logInfo: { category, contextName },
}) =>
    conditions &&
    conditions.every(({ type, attributeId }) => {
        if (['textual_attribute', 'attribute'].includes(type)) {
            const exist = !!filters.find(({ id }) => id === attributeId);
            if (!exist) {
                console.warn(
                    `From category ${category.name} (${category.id}), for ${contextName}, the ${attributeId} filter isn't present in category api`,
                );
            }
            return exist;
        }
        const key = MATCHING_FILTERS[type];

        return !!(typeof filtersConfig[key] === 'object');
    });

const checkAllQuickFiltersAttributesExist = ({
    rawQuickFilters,
    filters,
    filtersConfig,
    logInfo,
}) => {
    const conditions = getConditionsFromRawQuickFilters(rawQuickFilters);
    return checkAllConditionsExist({
        conditions,
        filters,
        filtersConfig,
        logInfo: {
            ...logInfo,
            contextName: 'QuickFilter',
        },
    });
};

const getRangeValuesFromCondition = ({
    condition = {},
    logInfo: { contextName, category },
}) => {
    let { value } = condition;

    if (condition.rule === 'between' && Array.isArray(condition.value)) {
        value = {
            min: parseFloat(condition.value[0], 10),
            max: parseFloat(condition.value[1], 10),
        };
    } else if (condition.rule === '<=' || condition.rule === 'less_equal') {
        value = {
            min: 0,
            max: parseFloat(condition.value, 10),
        };
    } else if (condition.rule === '>=' || condition.rule === 'greater_equal') {
        value = {
            min: parseFloat(condition.value, 10),
            max: undefined,
        };
    } else if (condition.rule === '=' || condition.rule === 'equal') {
        value = {
            min: parseFloat(condition.value, 10),
            max: parseFloat(condition.value, 10),
        };
    } else {
        console.warn(
            `From category ${category.name} (${category.id}), for ${contextName}, the "${condition.rule}" condition rule range is not supported`,
        );
    }
    return value;
};

const generateAttributesFromConditions = ({
    conditions = [],
    filters,
    filtersConfig,
    logInfo: { contextName, category },
}) =>
    conditions.map(condition => {
        let key;
        let type;
        let { value } = condition;
        if (['textual_attribute', 'attribute'].includes(condition.type)) {
            const matchFilter = filters.find(
                ({ id }) => id === condition.attributeId,
            );
            if (matchFilter) {
                ({ type } = matchFilter);
                key = `catalog_attribute.${condition.attributeId}`;
            }
        } else {
            key = MATCHING_FILTERS[condition.type];
            ({ type } = filtersConfig[key]);
        }

        if (type === 'range') {
            value = getRangeValuesFromCondition({
                condition,
                logInfo: { contextName, category },
            });
        } else if (type === undefined) {
            console.warn(
                `From category ${category.name} (${category.id}), for ${contextName}, the "${condition.type}" condition type is not supported`,
            );
        }

        return {
            key,
            operator: facetsUtils.getOperatorByFacetType(type),
            value,
            type,
        };
    });

const generateQuickFilters = ({
    rawQuickFilters,
    filters,
    filtersConfig,
    logInfo,
}) => {
    if (quickFiltersUtils.isQuickLink(rawQuickFilters)) {
        return {
            name: rawQuickFilters.name,
            title: rawQuickFilters.title,
            captionTitle: rawQuickFilters.captionTitle,
            quickLink: rawQuickFilters.quickLink,
            type: rawQuickFilters.type,
            pictureUrl: rawQuickFilters.pictureUrl,
        };
    }

    const conditions = getConditionsFromRawQuickFilters(rawQuickFilters);
    const attributes = generateAttributesFromConditions({
        conditions,
        filters,
        logInfo: { ...logInfo, contextName: 'QuickFilters' },
        filtersConfig,
    });

    return (
        (attributes &&
            rawQuickFilters && {
                attributes,
                name: rawQuickFilters && rawQuickFilters.name,
                title: rawQuickFilters && rawQuickFilters.title,
                captionTitle: rawQuickFilters && rawQuickFilters.captionTitle,
                type: rawQuickFilters.type,
                pictureUrl: rawQuickFilters.pictureUrl,
            }) ||
        null
    );
};

const getQuickFiltersFromCategoryContext = ({
    values,
    filters,
    filtersConfig,
    logInfo,
}) =>
    values
        .filter(
            rawQuickFilters =>
                quickFiltersUtils.isQuickLink(rawQuickFilters) ||
                checkAllQuickFiltersAttributesExist({
                    rawQuickFilters,
                    filters,
                    filtersConfig,
                    logInfo,
                }),
        )
        .map(rawQuickFilters =>
            generateQuickFilters({
                rawQuickFilters,
                filters,
                filtersConfig,
                logInfo,
            }),
        );

const getPromotedFiltersFromCategoryContext = ({
    values,
    filters,
    filtersConfig,
    logInfo,
}) => {
    const contextualizedLogInfo = {
        ...logInfo,
        contextName: 'PromotedFilters',
    };
    return values
        .filter(promotedFiltersStep => {
            return promotedFiltersStep.conditionGroups.every(pastille =>
                checkAllConditionsExist({
                    conditions: pastille.conditions,
                    filters,
                    filtersConfig,
                    logInfo: contextualizedLogInfo,
                }),
            );
        })
        .map(({ conditionGroups, ...otherData }) => {
            return {
                ...otherData,
                pastilles: conditionGroups.map(
                    ({ conditions, ...otherConditionGroupData }) => ({
                        attributes: generateAttributesFromConditions({
                            conditions,
                            filters,
                            logInfo: contextualizedLogInfo,
                            filtersConfig,
                        }),
                        ...otherConditionGroupData,
                    }),
                ),
            };
        });
};

export const prepareContext = ({
    category,
    categoryContext,
    filtersConfig,
}) => {
    const filters = getFiltersFromCategoryContext(categoryContext.filters);
    const quickFilters = getQuickFiltersFromCategoryContext({
        values: categoryContext.quick_filters,
        filters,
        filtersConfig,
        logInfo: {
            category: {
                id: category.id,
                name: category.name,
            },
        },
    });

    const promotedFilters = getPromotedFiltersFromCategoryContext({
        values: categoryContext.promoted_filters,
        filters,
        filtersConfig,
        logInfo: {
            category: {
                id: category.id,
                name: category.name,
            },
        },
    });

    /* eslint-disable camelcase */
    const {
        quick_filters,
        promoted_filters,
        ...cleanedCategoryContext
    } = categoryContext;
    /* eslint-enable camelcase */
    return {
        ...category,
        context: {
            ...cleanedCategoryContext,
            filters,
            quickFilters,
            promotedFilters,
        },
        name:
            category.name ||
            categoryContext.sibling_categories.find(
                cat => cat.id === category.id,
            ).label,
    };
};

/**
 * getAPIEndPoint : returns the right endpoint to call, depending on the user scenario
 * @categoryId {int} = id of the currentCategoryId
 * @isInCategorySearch {boolean} = determine if the user is currently in a inCategorySearch scenario
 * @items {array} = Algolia results
 */
export const getAPIEndPoint = ({
    categoryId = 0,
    isInCategorySearch,
    items = [],
}) => {
    if (!isInCategorySearch) return `ajax/categories/${categoryId}`;
    if (items.length === 0) return undefined;
    // Items are coming from Algolia, we don't want to display more than 10
    const trimmedItems = items.slice(0, 10);
    const qs = trimmedItems.map(item => item.label).join(',');
    return `ajax/category-previews/${qs}`;
};

/**
 * removeOutOfCategoryFilters
 * Remove specific category attributes to non corresponding category
 */
export const removeOutOfCategoryFilters = ({
    categoryId,
    currentCategoryId,
    specificFilters = [],
    selectedFilters = {},
}) => {
    // The legit category,OR no category detected, then nothing to do
    if (categoryId === currentCategoryId || !categoryId) return selectedFilters;
    // We need to remove specific filters but keep generic ones (like is_mmf)
    const allowedFilters = {};
    Object.keys(selectedFilters).forEach(currentSelectedFilter => {
        const isASpecificFilter = specificFilters.find(
            ({ id }) => currentSelectedFilter === `cat_attr_${id}`,
        );
        if (!isASpecificFilter)
            allowedFilters[currentSelectedFilter] =
                selectedFilters[currentSelectedFilter];
    });
    return allowedFilters;
};

/**
 * formatInCategorySearchData : will reformat the raw data to a unified structure
 * @data {object} : result from the ajax/category-previews API endpoint
 * @query {string} : current search query
 */
export const formatInCategorySearchData = ({
    data = [],
    query = '',
    selectedFilters = [],
    categoryId,
    specificFilters = [],
}) => {
    return data.map(({ id, imageUrl, label, url, level }) => {
        const allowedFilters = removeOutOfCategoryFilters({
            categoryId,
            currentCategoryId: id,
            specificFilters,
            selectedFilters,
        });
        const cleanedUrl = url.split('?')[0];
        return {
            label,
            id,
            imageUrl,
            level,
            slug: categorySlugifier(label, id),
            url: `${cleanedUrl}${
                query
                    ? `${queryString.stringify(
                          {
                              ...allowedFilters,
                              q: query,
                          },
                          { addQueryPrefix: true, format: 'RFC1738' },
                      )}`
                    : ''
            }`,
        };
    });
};

const concatFirstArrayWithSlicedSecondArray = (
    firstArray,
    secondeArray,
    startSlice,
    endSlice,
) => firstArray.concat(secondeArray.slice(startSlice, endSlice));

/**
 * formatCategoryData : will reformat the raw data to a unified structure
 * @categoryId {number} id of the current category, returned by ListingData
 * @data {object} : returned by the ajax/categories API endpoint
 */
export const formatCategoryData = ({
    categoryId = 0,
    data = {
        child_categories: [],
        sibling_categories: [],
        recommended_categories: [],
    },
}) => {
    const categoryToDisplay =
        data.child_categories?.length > 0
            ? 'child_categories'
            : 'sibling_categories';
    let trimmedData;

    if (data.recommended_categories?.length > 0) {
        trimmedData =
            data.recommended_categories.length > 5
                ? data.recommended_categories
                : concatFirstArrayWithSlicedSecondArray(
                      data.recommended_categories,
                      data[categoryToDisplay],
                      0,
                      10 - data.recommended_categories.length,
                  );
    } else {
        trimmedData = data[categoryToDisplay].slice(0, 10);
    }

    return trimmedData.map(
        // eslint-disable-next-line camelcase
        ({ id, label, picture_path, url }) => ({
            active: id === categoryId,
            label,
            id,
            imageUrl: picture_path,
            url,
        }),
    );
};

const SEARCH_SLUGS = {
    FR: 'recherche',
    GB: 'search',
    DE: 'suche',
    ES: 'busqueda',
    IT: 'ricerca',
};

export const formatSelectedCategoryData = ({
    category,
    query,
    platform,
    selectedFilters = {},
}) => {
    if (!category || !query) return [];
    const slug = SEARCH_SLUGS[platform.toUpperCase()];
    return [
        {
            active: true,
            id: category.id,
            imageUrl: '',
            label: category.name,
            url: `/${slug}/${query.replace(/ /g, '+')}${queryString.stringify(
                {
                    ...selectedFilters,
                    redirect: false,
                },
                { addQueryPrefix: true },
            )}`,
        },
    ];
};
/**
 * Returns a dummy array, filled with empty objects
 */
export const placeholderData = Array(10)
    .fill({}, 0, 10)
    .map((_, i) => ({
        id: i,
        imageUrl: '',
        label: '',
        url: '',
    }));
export const truncateCategoryName = (label = '', limit = 25) =>
    label.length > limit
        ? `${label.substring(0, limit).trim()}…`
        : label.trim();

export const getQueryFromLocation = (location = { params: {}, search: '' }) => {
    const { query } = location?.params || {};
    if (query) return query;
    const parsedSearch = queryString.parse(location?.search, {
        plainObjects: true,
        ignoreQueryPrefix: true,
    });
    if (parsedSearch.q) return parsedSearch.q;
    return undefined;
};

export const getNodesWidth = el => {
    const { width: parentWidth } = el?.parentNode?.getBoundingClientRect() || {
        width: 0,
    };
    const { width } = el?.getBoundingClientRect() || { width: 0 };
    return {
        parentWidth,
        width,
    };
};
export const fetchCategoryList = (apiEndpoint, options) => {
    const { baseUrl, certificates, Host } = options;
    if (!baseUrl || !apiEndpoint) return Promise.resolve({});
    return axios
        .get(`${baseUrl}/${apiEndpoint}`, {
            headers: {
                'Content-Type': 'application/json',
                ...(Host ? { Host } : {}),
                'X-Forwarded-Port': 443,
                'X-Forwarded-Proto': 'https',
            },
            httpsAgent: new https.Agent({
                rejectUnauthorized: false,
                ca: certificates,
            }),
        })
        .then(({ data }) => data);
};

export const isCategory = value =>
    !!value &&
    !!value.id &&
    !!value.context &&
    Object.keys(value.context).length > 0;

export default {
    fetchCategory,
    fetchInCategorySearch,
    prepareContext,
    MATCHING_FILTERS,
    EXPERIENCES,
    getAPIEndPoint,
    formatCategoryData,
    formatInCategorySearchData,
    formatSelectedCategoryData,
    placeholderData,
    truncateCategoryName,
    getQueryFromLocation,
    getNodesWidth,
    fetchCategoryList,
    isCategory,
};
